<template>
	<section class="ccard" :class="{ 'is-open': open }">
		<button
			type="button"
			class="ccard-head"
			:aria-expanded="open ? 'true' : 'false'"
			:aria-controls="bodyId"
			@click="toggle"
		>
			<span class="ccard-text">
				<span class="ccard-eyebrow">{{ eyebrow }}</span>
				<span class="ccard-title">{{ title }}</span>
			</span>
			<span v-if="summary" class="ccard-summary">{{ summary }}</span>
			<span class="ccard-chevron" aria-hidden="true"></span>
		</button>
		<div v-show="open" :id="bodyId" class="ccard-body">
			<slot />
		</div>
	</section>
</template>

<script>
// Tarjeta plegable de la pestaña «Consolidación»: la cabecera lleva el
// eyebrow, el título y un resumen (cuántos miembros, cuántas cuentas,
// cuántos neteos) para que, cerrada, siga informando. El estado es local:
// la pestaña decide con `defaultOpen` qué tarjetas nacen abiertas.
let seq = 0;

export default {
	name: "ConsolidationCard",
	props: {
		eyebrow: {
			type: String,
			required: true,
		},
		title: {
			type: String,
			required: true,
		},
		summary: {
			type: String,
			default: "",
		},
		defaultOpen: {
			type: Boolean,
			default: false,
		},
	},
	data() {
		seq += 1;
		return {
			open: this.defaultOpen,
			bodyId: `ccard-body-${seq}`,
		};
	},
	methods: {
		toggle() {
			this.open = !this.open;
			this.$emit("toggle", this.open);
		},
	},
};
</script>

<style lang="scss" scoped>
.ccard {
	background: var(--ap-paper);
	border: 1px solid var(--ap-line);
	border-radius: var(--ap-radius-sm);
	// Sin `overflow: hidden`: el panel desplegable de los selectores (cuentas
	// ignoradas, neteos) es absoluto y se recortaba en el borde de la tarjeta.
	// Las esquinas las respeta la cabecera con `border-radius: inherit`.
	overflow: visible;
	transition: border-color 0.16s var(--ap-ease), box-shadow 0.16s var(--ap-ease);

	&.is-open {
		border-color: var(--ap-line-strong);
		box-shadow: var(--ap-shadow-card);
	}
}

.ccard-head {
	appearance: none;
	display: flex;
	align-items: center;
	gap: 14px;
	width: 100%;
	padding: 14px 18px;
	border: 0;
	border-radius: inherit;
	background: transparent;
	text-align: left;
	font-family: var(--ap-font);
	color: var(--ap-ink-2);
	cursor: pointer;
	transition: background-color 0.14s var(--ap-ease);

	.is-open > & {
		border-bottom-left-radius: 0;
		border-bottom-right-radius: 0;
	}

	&:hover {
		background: var(--ap-mist);
	}

	&:focus-visible {
		outline: 2px solid var(--ap-teal);
		outline-offset: -2px;
	}
}

.ccard-text {
	display: flex;
	flex-direction: column;
	gap: 2px;
	min-width: 0;
	flex: 1;
}

.ccard-eyebrow {
	font-size: 11px;
	font-weight: 600;
	text-transform: uppercase;
	letter-spacing: 0.08em;
	color: var(--ap-muted);
}

.ccard-title {
	font-size: 16px;
	font-weight: 700;
	color: var(--ap-ink);
	overflow-wrap: anywhere;
}

.ccard-summary {
	flex: 0 0 auto;
	padding: 3px 10px;
	border-radius: var(--ap-radius-pill);
	background: var(--ap-mist);
	font-size: 12.5px;
	font-weight: 600;
	color: var(--ap-slate);
	font-variant-numeric: tabular-nums;

	.is-open & {
		background: var(--ap-teal-wash);
		color: var(--ap-teal-deep);
	}
}

// Chevron dibujado con bordes: gira al abrir.
.ccard-chevron {
	flex: 0 0 auto;
	width: 9px;
	height: 9px;
	border-right: 2px solid var(--ap-muted);
	border-bottom: 2px solid var(--ap-muted);
	transform: rotate(-45deg);
	transition: transform 0.18s var(--ap-ease), border-color 0.18s var(--ap-ease);

	.is-open & {
		transform: rotate(45deg);
		border-color: var(--ap-teal);
	}
}

.ccard-body {
	padding: 4px 18px 18px;
	border-top: 1px solid var(--ap-line);
}

@media (prefers-reduced-motion: reduce) {
	.ccard,
	.ccard-head,
	.ccard-chevron {
		transition: none;
	}
}
</style>
